import { create } from 'zustand';
import { ClaimStudioInstance, ScribeStudioInstance } from '@/api/models/studioInstance';
import { TokenResponse } from '@/api/reservoir';
import { IAnalytics } from '@/common/telemetry';
import { TransactionStep, TransactionStepState } from '@/common/types';
import { Claim } from '@/contracts/claimExtensionContract';

interface StoreState {
  /**
   * @important @dev there is only ever a studioInstance
   * or a scribeStudioInstance, never both.
   */

  /**
   * The core studio apps studio instance with all its public data.
   */
  studioInstance: ClaimStudioInstance | ScribeStudioInstance | undefined;

  /**
   * Stateful steps of the transactions the user will go through during checkout
   */
  txSteps: TransactionStep[];

  /**
   * The wallet address they have connected to the app.
   */
  walletAddress: string | undefined;

  /**
   * The network the collector is connected to
   */
  connectedNetwork: number | undefined;

  /**
   * Delegate.cash eligible vault wallet addresses to mint into
   */
  delegateAddresses: string[] | null;

  /**
   * The selected delegate address to use for allowlist check
   */
  selectedDelegateAddress: string;

  /**
   * The number of mints the collector wants to mint
   */
  numberOfMints: number;

  /**
   * Redemption code entered by the collector for gated mints
   */
  redemptionCode: string;

  /**
   * Crossmint is enabled
   */
  crossmintEnabled: boolean;

  /**
   * The address the collector wants to mint to.
   * Only set if gifting to someone or using a delegate wallet.
   */
  mintToAddress: string;

  /**
   * The email address the collector entered for the mint
   * Used to subscribe the collector to the creator's notifications
   */
  collectorEmail: string;

  /**
   * The on-chain Claim Extension data for this studio instance's ID
   * @dev Retrieved from the Maniold Bridge Provider on page load
   */
  claimData: Claim | null;

  /**
   * The information for the floor token that the collector can buy on the Reservoir-powered secondary market.
   * @dev Only set if:
   * 1. the sale has ended
   * 2. AND we can find a floor price listing for the token
   */
  reservoirFloorToken: TokenResponse | null;

  /**
   * The IDs of the tokens the collector has minted by using this app
   * and accepting all the transaction steps
   */
  mintedTokenIds: string[];

  /**
   * The transaction hash of the mint tx the collector has accepted
   */
  mintedTxHash: string;

  /**
   * Analytics instance for telemetry
   */
  analytics: IAnalytics | null;
}

interface StoreActions {
  setStudioInstance: (studioInstance: ClaimStudioInstance | ScribeStudioInstance) => void;
  setTxSteps: (txSteps: TransactionStep[]) => void;
  updateTxStepState: (index: number, updates: Partial<TransactionStepState>) => void;
  setWalletAddress: (walletAddress: string | undefined) => void;
  setConnectedNetwork: (connectedNetwork: number | undefined) => void;
  setMintedTokenIds: (tokenIds: string[]) => void;
  setMintedTxHash: (txHash: string) => void;
  setNumberOfMints: (numberOfMints: number) => void;
  setRedemptionCode: (redemptionCode: string) => void;
  setCrossmintEnabled: (crossmintEnabled: boolean) => void;
  setMintToAddress: (mintToAddress: string) => void;
  setCollectorEmail: (collectorEmail: string) => void;
  setClaimData: (claimData: Claim | null) => void;
  setReservoirFloorToken: (reservoirFloorToken: TokenResponse | null) => void;
  setDelegateAddresses: (delegateAddresses: string[] | null) => void;
  setSelectedDelegateAddress: (selectedDelegateAddress: string) => void;
  setAnalytics: (analytics: IAnalytics | null) => void;
  resetStoreAfterPurchase: () => void;
}

export type StoreType = StoreState & StoreActions;

/**
 * Main store hook for managing global state
 *
 * @example Accessing a store value
 * ```ts
 * const studioInstance = useMainStore(state => state.studioInstance);
 * ```
 *
 * @example Using a store action
 * ```ts
 * const setStudioInstance = useMainStore(state => state.setStudioInstance);
 * setStudioInstance(newInstance);
 * ```
 *
 * @example Multiple store values and actions
 * ```ts
 * const studioInstance = useMainStore(state => state.studioInstance);
 * const setStudioInstance = useMainStore(state => state.setStudioInstance);
 * ```
 */
const useMainStore = create<StoreType>((set) => ({
  /**
   * Default values
   */
  studioInstance: undefined,
  txSteps: [],
  walletAddress: undefined,
  connectedNetwork: undefined,
  mintedTokenIds: [],
  numberOfMints: 1,
  mintedTxHash: '',
  redemptionCode: '',
  crossmintEnabled: false,
  mintToAddress: '',
  collectorEmail: '',
  claimData: null,
  reservoirFloorToken: null,
  delegateAddresses: null,
  selectedDelegateAddress: '',
  analytics: null,

  /**
   * Setters
   */
  resetStoreAfterPurchase: () =>
    set({
      txSteps: [],
      mintedTokenIds: [],
      numberOfMints: 1,
      mintedTxHash: '',
      redemptionCode: '',
      mintToAddress: '',
      collectorEmail: '',
      selectedDelegateAddress: '',
    }),
  setStudioInstance: (studioInstance: ClaimStudioInstance | ScribeStudioInstance) =>
    set({ studioInstance }),
  setTxSteps: (txSteps: TransactionStep[]) => set({ txSteps }),
  setWalletAddress: (walletAddress: string | undefined) => set({ walletAddress }),
  setConnectedNetwork: (connectedNetwork: number | undefined) => set({ connectedNetwork }),
  setMintedTokenIds: (mintedTokenIds: string[]) => set({ mintedTokenIds }),
  setMintedTxHash: (mintedTxHash: string) => set({ mintedTxHash }),
  setNumberOfMints: (numberOfMints: number) => set({ numberOfMints }),
  setRedemptionCode: (redemptionCode: string) => set({ redemptionCode }),
  setCrossmintEnabled: (crossmintEnabled: boolean) => set({ crossmintEnabled }),
  setMintToAddress: (mintToAddress: string) => set({ mintToAddress }),
  setCollectorEmail: (collectorEmail: string) => set({ collectorEmail }),
  setClaimData: (claimData: Claim | null) => set({ claimData }),
  setReservoirFloorToken: (reservoirFloorToken: TokenResponse | null) =>
    set({ reservoirFloorToken }),
  updateTxStepState: (index: number, updates: Partial<TransactionStepState>) =>
    set((state) => ({
      txSteps: state.txSteps.map((step, i) =>
        i === index ? { ...step, state: { ...step.state, ...updates } } : step,
      ),
    })),
  setDelegateAddresses: (delegateAddresses: string[] | null) => set({ delegateAddresses }),
  setSelectedDelegateAddress: (selectedDelegateAddress: string) => set({ selectedDelegateAddress }),
  setAnalytics: (analytics: IAnalytics | null) => set({ analytics }),

  /**
   * @important
   * Need a dynamic getter from the store? Write a hook!
   * Where do you put it? In @/store/hooks.ts
   * This keeps data fresh while avoiding unnecessary re-renders.
   */
}));

export default useMainStore;
